import { OrganizationRiderDetail, OrganizationRiderPool } from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';

export class OrganizationRiderPoolUtil {
  /**
   * Handles display of the organization rider pool information applicable to user based on given organizationRiderDetail object.
   *
   * @param organizationRiderDetail
   * @returns {boolean}
   */
  static shouldDisplayOrganizationRiderPoolInfo(
    organizationRiderDetail: OrganizationRiderDetail,
    organizationRiderPools: Array<OrganizationRiderPool>
  ): boolean {
    if (isNullOrUndefined(organizationRiderDetail)) {
      return false;
    }

    if (isNullOrUndefined(organizationRiderDetail.organizationRiderPoolCode)) {
      return false;
    }

    if (organizationRiderPools.length === 0) {
      return false;
    }

    if (
      organizationRiderPools.length === 1 &&
      isNullOrUndefined(organizationRiderPools[0].rideLimit) &&
      isNullOrUndefined(organizationRiderPools[0].rideLimitPeriod)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Handles display of the organization rider pool selection field based on given organizationRiderPool object.
   *
   * @param organizationRiderPools
   * @param organizationId
   * @returns {boolean}
   */
  static shouldShowOrganizationRiderPoolSelection(organizationRiderPools: Array<OrganizationRiderPool>, organizationId: number): boolean {
    // If there is no organizationId to work with, do not show selection.
    if (isNullOrUndefined(organizationId)) {
      return false;
    }

    // If the rider pools array is null/undefined or empty, don't show selection.
    if (isNullOrUndefined(organizationRiderPools) || organizationRiderPools.length === 0) {
      return false;
    }

    // If the rider pool array has at least 1 entry, continue.
    if (organizationRiderPools.length > 0) {
      // Create an array with only rider pool options that apply to this specific organization.
      const riderPoolOptions = organizationRiderPools.filter(orp => orp.organizationId === organizationId);

      // If the available rider pool array only has one entry and that entry DOES NOT have a ride limit, don't show selection.
      if (riderPoolOptions.length === 1 && isNullOrUndefined(riderPoolOptions[0].rideLimit)) {
        return false;
      }
    }

    // If none of the above criteria are met, show rider pool selection.
    return true;
  }

  /**
   * Combines two separate organizationRiderPool arrays and returns a single array with only unique entries.
   *
   * @param currentOrganizationRiderPools
   * @param newOrganizationRiderPools
   * @returns {Array<OrganizationRiderPool>}
   */
  static storeUniqueOrganizationRiderPools(
    currentOrganizationRiderPools: Array<OrganizationRiderPool>,
    newOrganizationRiderPools: Array<OrganizationRiderPool>
  ): Array<OrganizationRiderPool> {
    const uniqueRiderPools = new Map([...newOrganizationRiderPools, ...currentOrganizationRiderPools].map(pool => [pool.id, pool]));

    return Array.from(uniqueRiderPools.values());
  }

  /**
   * Filters an array of multiple organization rider pools and returns only ones with a matching organization id.
   *
   * @param organizationRiderPools
   * @param organizationId
   * @returns {Array<OrganizationRiderPool>}
   */
  static filterOrganizationRiderPools(
    organizationRiderPools: Array<OrganizationRiderPool>,
    organizationId: number
  ): Array<OrganizationRiderPool> | null {
    // Create an array to store available rider pool options.
    let riderPoolOptions: Array<OrganizationRiderPool> = [];

    // If there is no organizationId to work with, do not show selection.
    if (isNullOrUndefined(organizationId)) {
      return null;
    }

    // If the rider pools array is null/undefined or empty, don't show selection.
    if (isNullOrUndefined(organizationRiderPools) || organizationRiderPools.length === 0) {
      return null;
    }

    // If the rider pool array has at least 1 entry, continue.
    if (organizationRiderPools.length > 0) {
      // Create an array with only rider pool options that apply to this specific organization.
      riderPoolOptions = organizationRiderPools.filter(orp => orp.organizationId === organizationId);

      // If the available rider pool array only has one entry and that entry DOES NOT have a ride limit, don't show selection.
      if (riderPoolOptions.length === 1 && isNullOrUndefined(riderPoolOptions[0].rideLimit)) {
        return null;
      }
    }

    // If none of the above criteria are met, populate the rider pool selection.
    return riderPoolOptions;
  }

  /**
   * Returns a string based on given organizationRiderPool ride limit information.
   *
   * @param organizationRiderPool
   * @returns {string}
   */
  static getOrganizationRiderPoolDisplayTextViaPool(organizationRiderPool: OrganizationRiderPool): string {
    if (!isNullOrUndefined(organizationRiderPool.rideLimit)) {
      const rideCountPlurality = organizationRiderPool?.rideLimit === 1 ? 'ride' : 'rides';

      return `${organizationRiderPool?.code} (${
        organizationRiderPool?.rideLimit
      } ${rideCountPlurality} per ${organizationRiderPool?.rideLimitPeriod?.name.toLowerCase()})`;
    }

    return organizationRiderPool.code;
  }

  /**
   * Returns a string based on given organizationRiderDetail ride limit information.
   *
   * @param organizationRiderDetail
   * @returns {string}
   */
  static getOrganizationRiderPoolDisplayTextViaDetail(organizationRiderDetail: OrganizationRiderDetail): string {
    if (!isNullOrUndefined(organizationRiderDetail.rideLimitDetail?.riderRideLimit)) {
      const rideCountPlurality = organizationRiderDetail.rideLimitDetail.riderRideLimit === 1 ? 'ride' : 'rides';

      return `${organizationRiderDetail.organizationRiderPoolCode} (${
        organizationRiderDetail.rideLimitDetail.riderRideLimit
      } ${rideCountPlurality} per ${organizationRiderDetail.rideLimitDetail.riderRideLimitPeriod.toLowerCase()})`;
    }

    return organizationRiderDetail.organizationRiderPoolCode;
  }
}
